// Report and block, for a profile that is not in a drawer.
//
// The peek drawer puts these in its footer, where there is a fixed bar to put
// them in. The full profile page has no such bar, so the same two actions live
// at the end of the profile instead — quiet until needed, which is the right
// weight for controls you hope nobody has to use, but findable in the one place
// someone would look for them: the profile of the person bothering them.
//
// Behaviour is shared with the drawer through useProfileSafety. Only the
// presentation differs.

import React from 'react';
import styled from 'styled-components';
import { useProfileSafety } from '../../../hooks/shared/useProfileSafety';

interface ProfileSafetyActionsProps {
  profileId: string;
  firstName: string;
  onNotify?: (type: 'match' | 'request' | 'info' | 'warning', message: string) => void;
  onBlocked?: (profileId: string) => void;
}

const Panel = styled.section`
  margin-top: 1.5rem;
  padding: 1.25rem;
  border-top: 1px solid var(--border-light, rgba(255, 255, 255, 0.1));
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Prompt = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  opacity: 0.7;
`;

const Action = styled.button<{ $variant?: 'danger' }>`
  padding: 0.6rem 1.1rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${props => (props.$variant === 'danger'
      ? 'rgba(239, 68, 68, 0.5)'
      : 'var(--border-light, rgba(255, 255, 255, 0.2))')};
  background: transparent;
  color: ${props => (props.$variant === 'danger' ? '#ef4444' : 'inherit')};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => (props.$variant === 'danger'
      ? 'rgba(239, 68, 68, 0.12)'
      : 'rgba(127, 127, 127, 0.12)')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
`;

const Reason = styled.button<{ $selected: boolean }>`
  text-align: left;
  padding: 0.65rem 0.9rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: inherit;
  border: 1px solid
    ${props => (props.$selected
      ? 'rgba(236, 72, 153, 0.8)'
      : 'var(--border-light, rgba(255, 255, 255, 0.15))')};
  background: ${props => (props.$selected ? 'rgba(236, 72, 153, 0.14)' : 'transparent')};
`;

const Note = styled.p`
  margin: 0 0 0.9rem;
  font-size: 0.8rem;
  opacity: 0.7;
`;

export const ProfileSafetyActions: React.FC<ProfileSafetyActionsProps> = ({
  profileId,
  firstName,
  onNotify,
  onBlocked,
}) => {
  const {
    reporting, setReporting,
    reportType, setReportType,
    submittingReport,
    confirmingBlock, setConfirmingBlock,
    blocking,
    block,
    submitReport,
    reasons,
  } = useProfileSafety({ profileId, firstName, onNotify, onBlocked });

  if (reporting) {
    return (
      <Panel aria-label={`Report ${firstName}`}>
        <Prompt>What is wrong with {firstName}'s profile?</Prompt>
        <ReasonList>
          {reasons.map(reason => (
            <Reason
              key={reason.type}
              $selected={reportType === reason.type}
              onClick={() => setReportType(reason.type)}
            >
              {reason.label}
            </Reason>
          ))}
        </ReasonList>
        {/* Said plainly, because the fear of the other person finding out is
            the most common reason a report never gets sent. */}
        <Note>Reports are private. {firstName} is not told.</Note>
        <Row>
          <Action
            onClick={() => { setReporting(false); setReportType(null); }}
            disabled={submittingReport}
          >
            Cancel
          </Action>
          <Action $variant="danger" onClick={submitReport} disabled={submittingReport || !reportType}>
            {submittingReport ? 'Sending…' : 'Send report'}
          </Action>
        </Row>
      </Panel>
    );
  }

  if (confirmingBlock) {
    return (
      <Panel aria-label={`Block ${firstName}`}>
        {/* Blocking is mutual and silent: neither of you can reach the other,
            and they are not told. */}
        <Prompt>
          Block {firstName}? Neither of you will be able to message or see each
          other. They are not told.
        </Prompt>
        <Row>
          <Action onClick={() => setConfirmingBlock(false)} disabled={blocking}>
            Cancel
          </Action>
          <Action $variant="danger" onClick={block} disabled={blocking}>
            {blocking ? 'Blocking…' : `Block ${firstName}`}
          </Action>
        </Row>
      </Panel>
    );
  }

  return (
    <Panel>
      <Row>
        <Action onClick={() => setReporting(true)} aria-label={`Report ${firstName}`}>
          Report
        </Action>
        <Action $variant="danger" onClick={() => setConfirmingBlock(true)} aria-label={`Block ${firstName}`}>
          Block
        </Action>
      </Row>
    </Panel>
  );
};
